import { createFileRoute } from "@tanstack/react-router";
import BlogPost from "@/pages/BlogPost";
import { staticBlogPosts } from "@/pages/Blog";
import { supabase } from "@/integrations/supabase/client";
import { seo, breadcrumbSchema, absoluteUrl, SITE_NAME } from "@/lib/seo";

export const Route = createFileRoute("/_site/blog/$slug")({
  // Runs on the server during SSR so the post's real title, description and
  // BlogPosting schema are in the HTML crawlers and link-preview bots receive.
  loader: async ({ params }) => {
    const { data } = await supabase
      .from("blog_posts")
      .select("title, excerpt, image_url, author, created_at, updated_at")
      .eq("slug", params.slug)
      .eq("published", true)
      .or("scheduled_at.is.null,scheduled_at.lte.now()")
      .maybeSingle();

    if (data) return data;

    const staticPost = staticBlogPosts.find((p) => p.slug === params.slug);
    if (!staticPost) return null;

    return {
      title: staticPost.title,
      excerpt: staticPost.excerpt,
      image_url: null,
      author: (staticPost as { author?: string }).author ?? SITE_NAME,
      created_at: staticPost.created_at,
      updated_at: staticPost.created_at,
    };
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return seo({
        title: "Post Not Found",
        description: "This blog post could not be found.",
        path: `/blog/${params.slug}`,
        noindex: true,
      });
    }

    const excerpt = loaderData.excerpt ?? "";
    const description =
      excerpt.length > 155 ? `${excerpt.slice(0, 152)}...` : excerpt;
    const url = absoluteUrl(`/blog/${params.slug}`);

    return seo({
      title: loaderData.title,
      description,
      path: `/blog/${params.slug}`,
      image: loaderData.image_url ?? undefined,
      type: "article",
      jsonLd: [
        breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Blog", path: "/blog" },
          { name: loaderData.title, path: `/blog/${params.slug}` },
        ]),
        {
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: loaderData.title,
          description,
          image: loaderData.image_url ?? undefined,
          datePublished: loaderData.created_at,
          dateModified: loaderData.updated_at ?? loaderData.created_at,
          mainEntityOfPage: { "@type": "WebPage", "@id": url },
          author: { "@type": "Person", name: loaderData.author ?? SITE_NAME },
          publisher: { "@type": "Organization", name: SITE_NAME },
        },
      ],
    });
  },
  component: BlogPost,
});
